import React, { useState } from 'react';

interface Facility {
  id?: number;
  name: string;
  address: string;
  responsible_person_id?: string;
  fda_registration_number?: string;
  annual_revenue?: number;
  employee_count?: number;
  facility_type: string;
  is_exempt?: boolean;
  exemption_reason?: string;
  registration_date?: string;
  renewal_date?: string;
}

interface FacilityFormProps {
  onSubmit: (facility: Omit<Facility, 'id' | 'registered_at' | 'next_renewal_date' | 'updated_at'>) => void;
  initialData?: Facility;
}

const FacilityForm: React.FC<FacilityFormProps> = ({ onSubmit, initialData }) => {
  const [formData, setFormData] = useState<Facility>({
    name: initialData?.name || '',
    address: initialData?.address || '',
    responsible_person_id: initialData?.responsible_person_id ? String(initialData.responsible_person_id) : '',
    fda_registration_number: initialData?.fda_registration_number || '',
    annual_revenue: initialData?.annual_revenue || undefined,
    employee_count: initialData?.employee_count || undefined,
    facility_type: initialData?.facility_type || 'manufacturing',
    is_exempt: initialData?.is_exempt || false,
    exemption_reason: initialData?.exemption_reason || '',
    registration_date: initialData?.registration_date || '',
    renewal_date: initialData?.renewal_date || '',
  });

  React.useEffect(() => {
    setFormData({
      name: initialData?.name || '',
      address: initialData?.address || '',
      responsible_person_id: initialData?.responsible_person_id ? String(initialData.responsible_person_id) : '',
      fda_registration_number: initialData?.fda_registration_number || '',
      annual_revenue: initialData?.annual_revenue || undefined,
      employee_count: initialData?.employee_count || undefined,
      facility_type: initialData?.facility_type || 'manufacturing',
      is_exempt: initialData?.is_exempt || false,
      exemption_reason: initialData?.exemption_reason || '',
      registration_date: initialData?.registration_date || '',
      renewal_date: initialData?.renewal_date || '',
    });
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Send backend snake_case keys to match API schema
    const payload: any = {
      name: formData.name,
      address: formData.address,
      // snake_case (primary)
      facility_type: formData.facility_type,
      responsible_person_id: formData.responsible_person_id ? String(formData.responsible_person_id) : null,
      fda_registration_number: formData.fda_registration_number,
      annual_revenue: typeof formData.annual_revenue === 'number' && !isNaN(formData.annual_revenue) ? formData.annual_revenue : undefined,
      employee_count: typeof formData.employee_count === 'number' && !isNaN(formData.employee_count) ? formData.employee_count : undefined,
      is_exempt: formData.is_exempt,
      exemption_reason: formData.exemption_reason,
      registration_date: formData.registration_date,
      renewal_date: formData.renewal_date,
      // camelCase aliases (for safety with alias-based parsing)
      facilityType: formData.facility_type,
      responsiblePersonId: formData.responsible_person_id ? String(formData.responsible_person_id) : null,
      fdaRegistrationNumber: formData.fda_registration_number,
      annualRevenue: typeof formData.annual_revenue === 'number' && !isNaN(formData.annual_revenue) ? formData.annual_revenue : undefined,
      employeeCount: typeof formData.employee_count === 'number' && !isNaN(formData.employee_count) ? formData.employee_count : undefined,
      isExempt: formData.is_exempt,
      exemptionReason: formData.exemption_reason,
      registrationDate: formData.registration_date,
      renewalDate: formData.renewal_date,
    };
    
    onSubmit(payload);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    const target = e.target as HTMLInputElement;
    
    // Special handling for responsible_person_id - always treat as string
    if (name === 'responsible_person_id') {
      const value = target.value;
      setFormData(prev => ({ ...prev, [name]: String(value) }));
      return;
    }
    
    const isNumber = target.type === 'number';
    if (isNumber) {
      const valNum = (target as HTMLInputElement).valueAsNumber;
      const numValue = isNaN(valNum) ? undefined : valNum;
      setFormData(prev => ({ ...prev, [name]: numValue }));
      return;
    }
    const value = target.value;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Auto-populate renewal_date when registration_date is set (default +1 year)
  React.useEffect(() => {
    if (formData.registration_date && !formData.renewal_date) {
      const d = new Date(formData.registration_date);
      if (!isNaN(d.getTime())) {
        const next = new Date(d);
        next.setFullYear(d.getFullYear() + 1);
        const iso = next.toISOString().slice(0, 10);
        setFormData(prev => ({ ...prev, renewal_date: iso }));
      }
    }
  }, [formData.registration_date, formData.renewal_date]);

  // Auto-calculate exemption based on thresholds (simple heuristic)
  React.useEffect(() => {
    const revenue = formData.annual_revenue ?? 0;
    const employees = formData.employee_count ?? 0;
    const shouldExempt = revenue <= 1000000 && employees <= 50;
    setFormData(prev => ({
      ...prev,
      is_exempt: prev.is_exempt ?? shouldExempt ? shouldExempt : prev.is_exempt,
    }));
  }, [formData.annual_revenue, formData.employee_count]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information Section */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
          Facility Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
              placeholder="Enter facility name"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900 placeholder-gray-500"
        />
      </div>
      <div>
            <label htmlFor="facility_type" className="block text-sm font-semibold text-gray-700 mb-2">
              Facility Type *
            </label>
            <select
              id="facility_type"
              name="facility_type"
              value={formData.facility_type}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900"
            >
              <option value="manufacturing">Manufacturing</option>
              <option value="packaging">Packaging</option>
              <option value="distribution">Distribution</option>
            </select>
          </div>
        </div>
        <div className="mt-6">
          <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
          Address *
        </label>
        <input
          type="text"
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
            placeholder="Enter complete address"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-gray-900 placeholder-gray-500"
        />
        </div>
      </div>

      {/* Registration & Compliance Section */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Registration & Compliance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
            <label htmlFor="fda_registration_number" className="block text-sm font-semibold text-gray-700 mb-2">
          FDA Registration Number
        </label>
        <input
          type="text"
          id="fda_registration_number"
          name="fda_registration_number"
          value={formData.fda_registration_number}
          onChange={handleChange}
              placeholder="e.g., FDA123456789"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500"
        />
      </div>
      <div>
            <label htmlFor="responsible_person_id" className="block text-sm font-semibold text-gray-700 mb-2">
          Responsible Person ID
        </label>
        <input
              type="text"
          id="responsible_person_id"
          name="responsible_person_id"
          value={formData.responsible_person_id || ''}
          onChange={handleChange}
              placeholder="Enter responsible person ID"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500"
        />
      </div>
      </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      <div>
            <label htmlFor="registration_date" className="block text-sm font-semibold text-gray-700 mb-2">
            Registration Date
          </label>
          <input
            type="date"
            id="registration_date"
            name="registration_date"
            value={formData.registration_date || ''}
            onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
          />
        </div>
        <div>
            <label htmlFor="renewal_date" className="block text-sm font-semibold text-gray-700 mb-2">
            Renewal Date
          </label>
          <input
            type="date"
            id="renewal_date"
            name="renewal_date"
            value={formData.renewal_date || ''}
            onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Business Information Section */}
      <div className="bg-green-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Business Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="annual_revenue" className="block text-sm font-semibold text-gray-700 mb-2">
              Annual Revenue ($)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="annual_revenue"
                name="annual_revenue"
                value={formData.annual_revenue ?? ''}
                onChange={handleChange}
                placeholder="500000"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-gray-900 placeholder-gray-500"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">Required for small business exemption calculation</p>
          </div>
          <div>
            <label htmlFor="employee_count" className="block text-sm font-semibold text-gray-700 mb-2">
              Number of Employees
            </label>
            <input
              type="number"
              id="employee_count"
              name="employee_count"
              value={formData.employee_count ?? ''}
              onChange={handleChange}
              placeholder="25"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-gray-900 placeholder-gray-500"
            />
            <p className="mt-2 text-sm text-gray-600">Required for small business exemption calculation</p>
          </div>
        </div>
      </div>


      {/* Exemption Section */}
      <div className="bg-yellow-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Exemption Status
        </h3>
        <div className="flex items-start space-x-3">
        <input
          id="is_exempt"
          name="is_exempt"
          type="checkbox"
          checked={!!formData.is_exempt}
          onChange={handleCheckboxChange}
            className="h-5 w-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 mt-1"
        />
          <div className="min-w-0 flex-1">
            <label htmlFor="is_exempt" className="text-sm font-semibold text-gray-700">
            Facility is exempt (Small Business Exemption)
          </label>
            <p className="text-sm text-gray-600 mt-1">Based on revenue and employee thresholds</p>
      {formData.is_exempt && (
              <div className="mt-4">
                <label htmlFor="exemption_reason" className="block text-sm font-semibold text-gray-700 mb-2">
            Exemption Reason
          </label>
                <textarea
            id="exemption_reason"
            name="exemption_reason"
            value={formData.exemption_reason || ''}
            onChange={handleChange}
                  placeholder="Provide detailed reason for exemption"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors text-gray-900 placeholder-gray-500 resize-none"
          />
        </div>
      )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
      <button
        type="submit"
          className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-3 rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        {initialData ? 'Update Facility' : 'Register Facility'}
      </button>
      </div>
    </form>
  );
};

export default FacilityForm; 